import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { ArrowLeft, ArrowRight, Clock } from "lucide-react";
import PageCTA from "@/components/PageCTA";
import { Reveal, MaskedLine, HighlandBackdrop } from "@/components/Reveal";

/* -------------------------------------------------------------- */
/*  Upcoming articles — placeholder grid for the Journal.         */
/*  Swap-out plan for each entry when the article is written:     */
/*    • change `status: "coming"` → `status: "live"`              */
/*    • set `slug` to the real post slug                          */
/*    • Read more will auto-link to `/blog/<slug>`                */
/*  Add or remove rows freely — grid re-flows automatically.      */
/* -------------------------------------------------------------- */
const UPCOMING_ARTICLES = [
    {
        id: "dji-air-3-review",
        category: "Gear",
        title: "Six months with the DJI Air 3",
        excerpt: "Battery real-world numbers, the 70mm lens I actually reach for, and the one accessory I now refuse to fly without.",
        readMinutes: 8,
        image: "https://images.pexels.com/photos/8938701/pexels-photo-8938701.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=600&w=900",
        status: "coming",
    },
    {
        id: "nc500-flight-plan",
        category: "Route Prep",
        title: "Planning the NC500 as a pilot, not a driver",
        excerpt: "Where to launch, where you legally can't, and the four stops most guides quietly leave off the list.",
        readMinutes: 12,
        image: "https://images.pexels.com/photos/33677969/pexels-photo-33677969.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=600&w=900",
        status: "coming",
    },
    {
        id: "weather-apps",
        category: "Gear",
        title: "The three weather apps I actually trust",
        excerpt: "Highland micro-climates ruin more shoots than gear ever does. Here's the stack that stops me driving 200 miles for nothing.",
        readMinutes: 6,
        image: "https://images.pexels.com/photos/19593823/pexels-photo-19593823.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=600&w=900",
        status: "coming",
    },
    {
        id: "skye-route",
        category: "Route Prep",
        title: "Skye in three days — a pilot's itinerary",
        excerpt: "The exact order I fly Storr, Quiraing, Fairy Pools and Neist Point to catch the best light and avoid the coach parties.",
        readMinutes: 10,
        image: "https://images.pexels.com/photos/6280898/pexels-photo-6280898.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=600&w=900",
        status: "coming",
    },
    {
        id: "cold-weather-flying",
        category: "Gear",
        title: "Cold-weather flying without wrecking a battery",
        excerpt: "How I get a full 20-minute flight out of the Air 3 in single-digit Highland temperatures — no gimmicks, no hand-warmers.",
        readMinutes: 7,
        image: "https://images.pexels.com/photos/29353711/pexels-photo-29353711.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=600&w=900",
        status: "coming",
    },
    {
        id: "cairngorms-permits",
        category: "Route Prep",
        title: "Flying legally in the Cairngorms",
        excerpt: "National park rules, land-owner permissions and the sensible-neighbour approach that keeps drones welcome in Scotland.",
        readMinutes: 9,
        image: "https://images.pexels.com/photos/16948691/pexels-photo-16948691.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=600&w=900",
        status: "coming",
    },
];

export function BlogList() {
    const [posts, setPosts] = useState([]);
    useEffect(() => { api.get("/blog").then((r) => setPosts(r.data)); }, []);

    return (
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32" data-testid="blog-list-page">
            <header className="mb-20 max-w-3xl relative">
                <Reveal>
                    <div className="overline mb-5"><span className="editorial-rule" />Journal</div>
                </Reveal>
                <h1
                    className="font-serif font-light tracking-tighter leading-[0.9]"
                    style={{ fontSize: "clamp(2.8rem, 7vw, 7rem)" }}
                    data-testid="blog-title"
                >
                    <div><MaskedLine delay={0.1}>Notes from</MaskedLine></div>
                    <div>
                        <MaskedLine delay={0.3}>
                            <span className="italic text-[var(--sb-brand)]">the field.</span>
                        </MaskedLine>
                    </div>
                </h1>
                <Reveal delay={0.45}>
                    <p className="text-[var(--sb-text-dim)] mt-8 text-lg leading-relaxed max-w-xl">
                        Field notes, tips, and one stubborn opinion about colour grading.
                    </p>
                </Reveal>
            </header>

            {posts.length > 0 && (
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-24">
                    <Reveal className="lg:col-span-7">
                        <Link to={`/blog/${posts[0].slug}`} className="group block" data-testid={`blog-feature-${posts[0].id}`}>
                            <div className="relative aspect-[16/10] overflow-hidden mb-6 border border-[var(--sb-border)]">
                                <img src={posts[0].cover} alt={posts[0].title} className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]" />
                                <div className="absolute inset-0 bg-gradient-to-t from-[var(--sb-bg)]/60 via-transparent to-transparent" />
                                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700" style={{ background: "radial-gradient(50% 40% at 50% 60%, rgba(107,167,222,0.18) 0%, rgba(0,0,0,0) 65%)" }} />
                            </div>
                            <div className="overline mb-3">{new Date(posts[0].published_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</div>
                            <h2 className="font-serif tracking-tight leading-[1.02] mb-4 group-hover:text-[var(--sb-brand)] transition-colors" style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)" }}>
                                {posts[0].title}
                            </h2>
                            <p className="text-[var(--sb-text-dim)] max-w-xl text-lg">{posts[0].excerpt}</p>
                        </Link>
                    </Reveal>
                    <div className="lg:col-span-5 flex flex-col gap-8">
                        {posts.slice(1).map((p, i) => (
                            <Reveal key={p.id} delay={0.1 + i * 0.08}>
                                <Link to={`/blog/${p.slug}`} className="group block pb-8 border-b border-[var(--sb-border)] last:border-0" data-testid={`blog-item-${p.id}`}>
                                    <div className="overline mb-3">{new Date(p.published_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</div>
                                    <h3 className="font-serif text-2xl lg:text-3xl tracking-tight leading-tight mb-3 group-hover:text-[var(--sb-brand)] transition-colors">{p.title}</h3>
                                    <p className="text-[var(--sb-text-dim)] text-sm leading-relaxed">{p.excerpt}</p>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            )}

            {/* -------- Upcoming articles grid ----------------------- */}
            <section className="mb-24" data-testid="upcoming-articles">
                <Reveal>
                    <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
                        <div>
                            <div className="overline mb-3" style={{ color: "#0055A4", fontWeight: 700 }}>
                                <span className="editorial-rule" style={{ background: "#0055A4" }} />
                                Coming to the Journal
                            </div>
                            <h2
                                className="font-serif tracking-tight leading-[0.95]"
                                style={{ fontSize: "clamp(2rem, 4.4vw, 3.6rem)" }}
                            >
                                In the flight bag,<br/>
                                <span className="italic" style={{ color: "#0055A4" }}>and on the map.</span>
                            </h2>
                        </div>
                        <p className="text-[var(--sb-text-dim)] max-w-md text-base leading-relaxed">
                            Deep dives on the kit I actually fly and the routes I&rsquo;m planning next.
                        </p>
                    </div>
                </Reveal>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="upcoming-articles-grid">
                    {UPCOMING_ARTICLES.map((a, i) => {
                        const live = a.status === "live";
                        const Wrapper = live ? Link : "div";
                        const wrapperProps = live ? { to: `/blog/${a.slug || a.id}` } : {};
                        return (
                            <Reveal key={a.id} delay={0.05 + (i % 3) * 0.06}>
                                <article
                                    className="group flex flex-col h-full border border-[var(--sb-border)] hover:border-[var(--sb-brand)]/60 transition-colors overflow-hidden"
                                    data-testid={`upcoming-article-${a.id}`}
                                >
                                    <Wrapper {...wrapperProps} className="block overflow-hidden aspect-[16/10] relative">
                                        <img
                                            src={a.image}
                                            alt={a.title}
                                            className={`w-full h-full object-cover transition-transform duration-[1200ms] ease-out ${live ? "group-hover:scale-[1.06]" : ""}`}
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-[var(--sb-bg)]/70 via-transparent to-transparent" />
                                        <span
                                            className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.28em] px-2.5 py-1 text-white"
                                            style={{ background: "#0055A4" }}
                                        >
                                            {a.category}
                                        </span>
                                        {!live && (
                                            <span
                                                className="absolute top-3 right-3 text-[10px] uppercase tracking-[0.28em] px-2.5 py-1 bg-white/85 backdrop-blur-sm"
                                                style={{ color: "#0055A4", fontWeight: 700 }}
                                                data-testid={`upcoming-badge-${a.id}`}
                                            >
                                                Coming soon
                                            </span>
                                        )}
                                    </Wrapper>
                                    <div className="p-6 flex flex-col flex-1">
                                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-[var(--sb-text-muted)] mb-3">
                                            <Clock size={11} /> {a.readMinutes} min read
                                        </div>
                                        <h3 className="font-serif text-xl lg:text-2xl tracking-tight leading-[1.15] mb-3">
                                            {a.title}
                                        </h3>
                                        <p className="text-sm text-[var(--sb-text-dim)] leading-relaxed mb-6 flex-1">
                                            {a.excerpt}
                                        </p>
                                        {live ? (
                                            <Link
                                                to={`/blog/${a.slug || a.id}`}
                                                className="btn-primary self-start text-xs"
                                                style={{ padding: "0.55rem 1.1rem" }}
                                                data-testid={`upcoming-read-${a.id}`}
                                            >
                                                Read more <ArrowRight size={14} />
                                            </Link>
                                        ) : (
                                            <button
                                                type="button"
                                                disabled
                                                aria-disabled="true"
                                                className="self-start inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] border border-[var(--sb-border)] px-4 py-2.5 text-[var(--sb-text-muted)] cursor-not-allowed"
                                                data-testid={`upcoming-read-${a.id}`}
                                            >
                                                Read more <ArrowRight size={14} />
                                            </button>
                                        )}
                                    </div>
                                </article>
                            </Reveal>
                        );
                    })}
                </div>
            </section>

            <PageCTA variant="story" className="!px-0" />
            <HighlandBackdrop variant="soft" />
        </div>
    );
}

export function BlogPost() {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setError("Post not found."));
    }, [slug]);

    if (error) return (
        <div className="max-w-3xl mx-auto px-6 py-32 text-center">
            <p className="text-[var(--sb-text-dim)] mb-6">{error}</p>
            <Link to="/blog" className="btn-outline"><ArrowLeft size={18}/> Back to journal</Link>
        </div>
    );
    if (!post) return <div className="max-w-3xl mx-auto px-6 py-32 text-center text-[var(--sb-text-dim)]">Loading…</div>;

    return (
        <article className="max-w-4xl mx-auto px-6 lg:px-12 py-20 lg:py-28" data-testid="blog-post-page">
            <Link to="/blog" className="inline-flex items-center gap-2 text-[var(--sb-text-dim)] hover:text-[var(--sb-text)] text-sm uppercase tracking-wider mb-10"><ArrowLeft size={14}/> All notes</Link>
            <div className="overline mb-3">{new Date(post.published_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })} · {post.author}</div>
            <h1 className="font-serif text-4xl lg:text-6xl tracking-tighter leading-[1] mb-10">{post.title}</h1>
            {post.cover && (
                <div className="aspect-[16/9] overflow-hidden mb-12">
                    <img src={post.cover} alt={post.title} className="w-full h-full object-cover" />
                </div>
            )}
            <div className="prose prose-invert max-w-none text-[var(--sb-text-dim)] text-lg leading-[1.85] space-y-6">
                {post.body.split("\n\n").map((para, i) => (
                    <p key={i}>{para}</p>
                ))}
            </div>
            <div className="mt-16 pt-8 border-t border-[var(--sb-border)] flex flex-wrap gap-2">
                {post.tags?.map((t) => <span key={t} className="text-xs uppercase tracking-[0.2em] text-[var(--sb-brand)] border border-[var(--sb-brand)] px-3 py-1">{t}</span>)}
            </div>
            <div className="mt-12 card p-8 lg:p-10 text-center" data-testid="blog-gofundme-cta" style={{background: "linear-gradient(135deg, rgba(80,101,115,0.12) 0%, rgba(138,123,102,0.06) 100%)"}}>
                <div className="overline mb-3">Back the Expedition</div>
                <h3 className="font-serif text-3xl lg:text-4xl tracking-tight mb-4 leading-tight">Help fund the NC500 mission.</h3>
                <p className="text-[var(--sb-text-dim)] max-w-xl mx-auto mb-8">
                    516 miles to prove disabled creators can hit the commercial standard — and to raise vital funds for the MS Society. Every contribution puts the beast in the air.
                </p>
                <div className="flex justify-center gap-3 flex-wrap">
                    <a href="https://gofund.me/da1365285" target="_blank" rel="noreferrer" className="btn-primary" data-testid="blog-back-expedition">
                        Back the Expedition <ArrowRight size={18} />
                    </a>
                    <Link to="/subscription" className="btn-outline">Subscribe — £1.89</Link>
                </div>
            </div>
        </article>
    );
}
